import React, { useState } from 'react';
import { apiService } from '../services/apiService';
import { trackClick } from '../utils/analytics';

interface ChatFeedbackProps {
    sessionId: string;
    messageId?: string;
    onSubmitted?: () => void;
}

export const ChatFeedback: React.FC<ChatFeedbackProps> = ({
    sessionId,
    messageId,
    onSubmitted
}) => {
    const [rating, setRating] = useState<number | null>(null);
    const [showFeedbackText, setShowFeedbackText] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleRatingClick = (selectedRating: number) => {
        trackClick('search_click_rating', { stars: selectedRating });
        setRating(selectedRating);
        setShowFeedbackText(true);
    };

    const handleSubmit = async () => {
        if (!rating) return;

        console.log('🔍 ChatFeedback Debug:', {
            sessionId,
            messageId,
            rating,
            feedbackText: feedbackText.trim()
        });

        setIsSubmitting(true);
        try {
            const response = await apiService.submitChatFeedback(
                sessionId,
                messageId,
                rating,
                feedbackText.trim() || undefined
            );

            if (response.success) {
                setIsSubmitted(true);
                setTimeout(() => {
                    onSubmitted?.();
                }, 2000);
            } else {
                console.error('Failed to submit feedback:', response.error);
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-green-700 font-grotesk text-sm">Thank you for your feedback!</span>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-grayscale-1200 font-grotesk text-sm font-medium">
                        How would you rate this response?
                    </span>
                </div>

                {/* 5-star rating */}
                <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            onClick={() => handleRatingClick(star)}
                            className="p-1 hover:scale-110 transition-transform"
                            disabled={isSubmitting}
                        >
                            <svg
                                className={`w-5 h-5 ${
                                    rating && star <= rating
                                        ? 'text-yellow-400 fill-current'
                                        : 'text-gray-300 hover:text-yellow-300'
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.519-4.674z"
                                />
                            </svg>
                        </button>
                    ))}
                    {rating && (
                        <span className="ml-2 text-grayscale-1000 font-grotesk text-sm">
                            {rating} star{rating !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                {/* Feedback text area */}
                {showFeedbackText && (
                    <div className="flex flex-col gap-2">
                        <textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Tell us more about your experience (optional)"
                            className="w-full p-2 border border-gray-300 rounded-md text-sm font-grotesk resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            rows={3}
                            maxLength={500}
                        />
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">
                                {feedbackText.length}/500 characters
                            </span>
                            <button
                                onClick={handleSubmit}
                                disabled={!rating || isSubmitting}
                                className="px-4 py-2 bg-primary-og-600 text-white font-grotesk text-sm font-medium rounded-full hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
